import React, { useEffect, useState } from 'react';
import { Zap } from 'lucide-react';
import { useChat } from '@hooks/useChat.ts';
import { AssistantSelector } from '@components/assistant';
import { ChatHistoryMenu } from '../history/ChatHistoryMenu';
import { loadAssistants } from '@config/assistants.config.ts';
import { StorageService } from '@services/storage/StorageService.ts';
import { Assistant } from '@/types';
import GPTLogoWhite from '@/assets/GPTforme Logo White.svg';
import { renderAssistantIcon } from '@/utils/RenderAssistantIcon';

export const Header: React.FC = () => {
    const { selectedAssistant, setSelectedAssistant, startNewChat } = useChat();
    const [assistants, setAssistants] = useState<Record<string, Assistant>>({});
    const [loadingAssistants, setLoadingAssistants] = useState(true);
    const storageService = StorageService.getInstance();
    const [userStreak, setUserStreak] = useState(0);

    useEffect(() => {
        const fetchAssistants = async () => {
            try {
                const loadedAssistants = await loadAssistants();
                setAssistants(loadedAssistants);
            } catch (error) {
                console.error('Failed to load assistants:', error);
            } finally {
                setLoadingAssistants(false);
            }
        };
        fetchAssistants();
    }, []);

    const assistant = selectedAssistant ? assistants[selectedAssistant] : null;

    const extractColorsFromGradient = (gradient: string): string[] => {
        const colorMatches = gradient.match(/#[0-9a-fA-F]{3,6}|rgb\([^)]+\)|rgba\([^)]+\)|hsl\([^)]+\)|hsla\([^)]+\)/g);
        return colorMatches || [];
    };

    const getIconColor = () => {
        if (!assistant?.bgGradient) return '#22d3ee';
        const gradientColors = extractColorsFromGradient(assistant.bgGradient);
        return gradientColors[0] || assistant.accentColor || '#ffffff';
    };

    const handleLogoClick = (e: React.MouseEvent) => {
        e.preventDefault();
        setSelectedAssistant('');
        startNewChat();
    };

    const renderCenterContent = () => {
        // Don't show anything while loading or if no assistant is selected
        if (loadingAssistants || !assistant) {
            return null;
        }

        return (
            <div className="hidden sm:flex absolute left-1/2 transform -translate-x-1/2 items-center gap-2">
                <div className={`p-1.5 sm:p-2 rounded-full bg-gradient-to-r ${assistant.bgGradient || 'from-blue-500 to-blue-600'}`}>
                    {renderAssistantIcon({
                        assistant,
                        iconColor: getIconColor(),
                        size: 'lg'
                    })}
                </div>
                <div className="hidden md:block">
                    <h1 className="text-base md:text-lg font-bold text-white">{assistant.name || 'AI Assistant'}</h1>
                    <p className="text-xs text-gray-300">{assistant.description || 'Here to help'}</p>
                </div>
            </div>
        );
    };

    useEffect(() => {
        const today = new Date().toDateString();
        const lastVisit = storageService.getLastVisit();
        const savedStreak = storageService.getUserStreak();

        if (lastVisit === today) {
            setUserStreak(savedStreak);
        } else if (lastVisit) {
            const yesterday = new Date();
            yesterday.setDate(yesterday.getDate() - 1);
            if (lastVisit === yesterday.toDateString()) {
                const newStreak = savedStreak + 1;
                setUserStreak(newStreak);
                storageService.updateUserStreak(newStreak);
            } else {
                setUserStreak(1);
                storageService.updateUserStreak(1);
            }
        } else {
            setUserStreak(1);
            storageService.updateUserStreak(1);
        }
        storageService.updateLastVisit(today);
    }, []);

    return (
        <div className="fixed top-0 left-0 right-0 bg-[#1a1a1a] border-b border-gray-600 z-10">
            <div className="w-full px-2 sm:px-4 md:px-6 lg:px-8 py-2 sm:py-3 md:py-4">
                <div className="flex items-center justify-between w-full relative">
                    {/* Logo */}
                    <div className="flex-shrink-0">
                        <a
                            href="#"
                            onClick={handleLogoClick}
                            className="block transition-opacity hover:opacity-80"
                        >
                            <img
                                src={GPTLogoWhite}
                                alt="GPTforme Logo"
                                className="h-6 sm:h-8 md:h-9 lg:h-10 w-auto cursor-pointer"
                                onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                }}
                            />
                        </a>
                    </div>

                    {/* Center content - hidden on mobile */}
                    {renderCenterContent()}

                    {/* Right section */}
                    <div className="flex items-center gap-1 sm:gap-2 md:gap-3 flex-shrink-0">
                        {userStreak > 1 && (
                            <div className="hidden lg:flex items-center gap-1 px-3 py-1 bg-amber-100 rounded-full">
                                <Zap className="w-4 h-4 text-amber-600" />
                                <span className="text-sm font-medium text-amber-700">
                                    {userStreak} day streak!
                                </span>
                            </div>
                        )}

                        {/* Only show AssistantSelector button when an assistant is selected */}
                        {assistant && (
                            <div className="bg-cyan-400 hover:bg-cyan-500 hover:shadow-lg text-black font-semibold rounded-full transition-all duration-300 ease-in-out px-2 sm:px-3 md:px-4 py-1 sm:py-1.5 md:py-2 transform hover:scale-105">
                                <AssistantSelector />
                            </div>
                        )}
                    </div>
                </div>

                {/* Mobile-only assistant info */}
                {assistant && (
                    <div className="sm:hidden mt-2 flex items-center gap-2 justify-center">
                        <div className={`p-1 rounded-full bg-gradient-to-r ${assistant.bgGradient || 'from-blue-500 to-blue-600'}`}>
                            {renderAssistantIcon({
                                assistant,
                                iconColor: getIconColor(),
                                size: 'md'
                            })}
                        </div>
                        <div>
                            <h1 className="text-xs font-bold text-white">{assistant.name || 'AI Assistant'}</h1>
                            <p className="text-[10px] text-gray-300 line-clamp-1">{assistant.description || 'Here to help'}</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};